import type { Timezone } from "@@/shared/date-time/timezone";
import { isDate } from "date-fns";
import {
    fromZonedTime as fromZonedTime_dateFnsTz,
    type ToDateOptionsWithTZ,
    toZonedTime as toZonedTime_dateFnsTz,
} from "date-fns-tz";

// Create ZonedDate, it should be a Date but with a timezone attached to it
// so that we can convert it to a regular date in the date environments
// real timezone to get the correct utc time

/**
 * A date object with a timezone attached to it.
 */
class ZonedDate extends Date {
    public timezone: Timezone;
    constructor(date: Date | string | number, timezone: Timezone) {
        super(date instanceof Date ? date.getTime() : date);
        this.timezone = timezone;
    }
}

const isZonedDate = (value: unknown): value is ZonedDate => {
    if (!value) return false;
    const valueIsDate = isDate(value);
    if (!valueIsDate) return false;
    if (!("timezone" in value)) return false;
    if (typeof value.timezone !== "string") return false;
    return true;
};

const isZonedDateInTimezone = (
    zoneDate: unknown,
    timezone: Timezone,
): zoneDate is ZonedDate => {
    return isZonedDate(zoneDate) && zoneDate.timezone === timezone;
};

const timezoneMatch = (
    zonedDate1: ZonedDate,
    zonedDate2: ZonedDate,
): boolean => {
    return zonedDate1.timezone === zonedDate2.timezone;
};

/**
 * Converts a given date to a specified time zone.
 *
 * @template Timezone - The type of the time zone string.
 * @param {Date | string | number} date - The date to be converted. It can be a Date object, a string, or a number.
 * @param {Timezone} timezone - The time zone to convert the date to.
 * @param {ToDateOptionsWithTZ} [options] - Optional configuration options for the conversion.
 * @returns {ZonedDate<Timezone>} - The date converted to the specified time zone, with the time zone information attached.
 */
const toZonedTime = (
    date: Date | ZonedDate | string | number,
    timezone: Timezone,
    options?: ToDateOptionsWithTZ,
): ZonedDate => {
    const _originallyZoned = date instanceof ZonedDate;
    if (_originallyZoned && date.timezone === timezone) return date;

    const _date = _originallyZoned
        ? toZonedTime_dateFnsTz(fromZonedDate(date), timezone, {
              ...options,
              timeZone: timezone,
          })
        : toZonedTime_dateFnsTz(date, timezone, {
              ...options,
              timeZone: timezone,
          });

    return Object.assign(_date, { timezone });
};

/**
 * Converts a `ZonedDate` object to a regular JavaScript `Date` object.
 *
 * @template Timezone - The type of the timezone string.
 * @param zonedDate - The `ZonedDate` object to convert.
 * @param options - Optional configuration for the conversion.
 * @returns A JavaScript `Date` object representing the same point in time as the input `ZonedDate`.
 */
const fromZonedDate = (
    zonedDate: ZonedDate,
    options?: ToDateOptionsWithTZ,
): Date => {
    const timezone = zonedDate.timezone;
    const result = fromZonedTime_dateFnsTz(zonedDate, timezone, options);
    return result;
};

const zoneDateUtils = {
    isZonedDate,
    isZonedDateInTimezone,
    toZonedTime,
    fromZonedDate,
    timezoneMatch,
};

export {
    fromZonedDate,
    isZonedDate,
    isZonedDateInTimezone,
    timezoneMatch,
    toZonedTime,
    zoneDateUtils,
    ZonedDate,
};
